import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAlertDefinitionOpenIncidents } from '@/hooks/useAlertDefinitions';

/** What the user is about to do to the check. */
export type DispositionAction = 'pause' | 'delete';

interface Props {
  open: boolean;
  action: DispositionAction;
  taskId: string | null;
  taskName: string;
  busy?: boolean;
  onCancel: () => void;
  /** `resolveIncidents` is the user's answer, not a default we chose for them. */
  onConfirm: (resolveIncidents: boolean) => void;
}

/**
 * Asks what should happen to a check's open incidents before pausing or deleting it.
 *
 * Neither answer is right in general, which is why this asks rather than assumes. Pausing
 * for a maintenance window should usually leave incidents alone; deleting a check nobody
 * wants usually means its incidents are noise too. So the *default* differs by verb while
 * both remain available.
 *
 * The incidents are listed, not counted. "3 open incidents" is a number; three titles are a
 * decision — the user can see at a glance whether they are stale noise or the thing they
 * were about to go and fix.
 */
export function AlertIncidentDisposition({
  open,
  action,
  taskId,
  taskName,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  const { data, isLoading } = useAlertDefinitionOpenIncidents(open ? taskId : null);
  const incidents = data?.incidents ?? [];
  const total = data?.total ?? 0;

  const verb = action === 'delete' ? 'Delete' : 'Pause';
  // Deleting a check usually means its incidents are unwanted too; pausing is usually
  // temporary and they should survive it. Both buttons are present either way.
  const resolveIsPrimary = action === 'delete';
  const leaveOpenIsPrimary = !resolveIsPrimary;
  const destructive = action === 'delete';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {verb} “{taskName}”
          </DialogTitle>
          <DialogDescription>
            {isLoading ? (
              'Checking what this alert has open…'
            ) : total === 0 ? (
              action === 'delete' ? (
                'This alert has no open incidents. Deleting it keeps its history.'
              ) : (
                'This alert has no open incidents.'
              )
            ) : (
              <>
                This alert has {total} open incident{total === 1 ? '' : 's'}. What should
                happen to {total === 1 ? 'it' : 'them'}?
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : total > 0 ? (
          <>
            <ul className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
              {incidents.map((incident) => (
                <li key={incident.id} className="flex items-start gap-2 p-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sev-2" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="truncate text-sm">{incident.title}</div>
                    <div className="text-xs text-muted-foreground">
                      Opened {new Date(incident.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <Badge className="ml-auto border-0 font-normal capitalize">
                    {incident.severity}
                  </Badge>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {action === 'delete'
                ? 'Left open, they stay visible and are marked as having no check behind them — nothing will close them automatically.'
                : 'Left open, they stay as they are and this alert can close them normally once it is resumed.'}
            </p>
          </>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          {total === 0 ? (
            // Nothing to decide about; just the action itself.
            <Button variant={destructive ? 'destructive' : 'default'} onClick={() => { onConfirm(false); }} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {verb}
            </Button>
          ) : (
            <>
              {/* Both choices are always offered; only which one leads differs. */}
              <Button
                variant={leaveOpenIsPrimary ? 'default' : 'outline'}
                onClick={() => { onConfirm(false); }}
                disabled={busy}
              >
                {busy && !resolveIsPrimary ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {verb}, leave {total === 1 ? 'it' : 'them'} open
              </Button>
              <Button
                variant={resolveIsPrimary ? (destructive ? 'destructive' : 'default') : 'outline'}
                onClick={() => { onConfirm(true); }}
                disabled={busy}
              >
                {busy && resolveIsPrimary ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {verb} and resolve {total === 1 ? 'it' : 'them'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
